import {useEffect, useState} from 'react';
import {detectEnvironment, shouldShowAllDocs} from '@/config/environment';
import type {DocsManifestEntry} from '@/types/docsManifest';
import {
    buildDocMenuSections,
    DOC_QUERY_PARAM,
    filterManualsForRoute,
    resolveDocFromQuery,
    updateViewerUrl,
} from './docsMenuUtils';

function readRequestedDoc(): string | null {
    return new URLSearchParams(window.location.search).get(DOC_QUERY_PARAM);
}

export function useDocsCatalog(activeRoles: string[], pathname: string) {
    const [entries, setEntries] = useState<DocsManifestEntry[] | null>(null);
    const [requestedDoc, setRequestedDoc] = useState<string | null>(() => readRequestedDoc());

    useEffect(() => {
        let cancelled = false;

        fetch('/docs/docs-manifest.json')
            .then(res => {
                if (!res.ok) throw new Error('not found');
                return res.json();
            })
            .then((data: DocsManifestEntry[]) => {
                if (!cancelled) {
                    setEntries(data);
                }
            })
            .catch(() => {
                if (!cancelled) {
                    setEntries([]);
                }
            });

        return () => {
            cancelled = true;
        };
    }, []);

    useEffect(() => {
        const handlePopState = () => {
            setRequestedDoc(readRequestedDoc());
        };

        window.addEventListener('popstate', handlePopState);
        return () => {
            window.removeEventListener('popstate', handlePopState);
        };
    }, []);

    const env = detectEnvironment();
    const visibleEntries = shouldShowAllDocs()
        ? (entries ?? [])
        : (entries ?? []).filter(entry => {
            if (env === 'DEV') return true;
            if (env === 'UAT') return entry.type !== 'technical';
            if (entry.type !== 'user-manual') return false;
            if (!entry.role) return true;
            return activeRoles.includes(entry.role);
        });

    // Menu only lists feature manuals relevant to the current route; deep-link
    // resolution stays unscoped so a shared `?doc=` link always opens.
    const sections = buildDocMenuSections(filterManualsForRoute(visibleEntries, pathname));
    const selectedDoc = resolveDocFromQuery(visibleEntries, requestedDoc);

    const openDoc = (entry: DocsManifestEntry) => {
        setRequestedDoc(entry.file);
        updateViewerUrl(entry.file);
    };

    const closeDoc = () => {
        setRequestedDoc(null);
        updateViewerUrl(null);
    };

    return {
        loaded: entries !== null,
        sections,
        selectedDoc,
        openDoc,
        closeDoc,
    };
}
